import { type FC } from "react";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import useUserDataProvider from "src/contexts/snug/userData-context";
import { useRouter } from "src/hooks/use-router";
import { paths } from "src/paths";

/**
 * Banner shown when a professional group has completed onboarding
 * (onboarding_completed_at is set) but has no Stripe Connect account on file
 * (stripe_connect_id is empty), so they can't process client payments. Only the
 * billing contact sees it, since they're the one who can complete the setup.
 */
export const StripeConnectMissingBanner: FC = () => {
  const { professionalGroup, userData } = useUserDataProvider();
  const router = useRouter();

  const isBillingContact =
    !!userData?.professional_group_role_user_data?.is_billing_contact;
  const isOnboarded = !!professionalGroup?.onboarding_completed_at;
  const hasStripeConnect = !!professionalGroup?.stripe_connect_id;

  if (!isBillingContact || !isOnboarded || hasStripeConnect) {
    return null;
  }

  return (
    <Box sx={{ px: { xs: 2, sm: 3 }, pt: 2 }}>
      <Alert
        severity="warning"
        variant="filled"
        action={
          <Button
            color="inherit"
            variant="outlined"
            size="small"
            onClick={() => router.push(paths.dashboard.accountTab("billing"))}
            sx={{ whiteSpace: "nowrap" }}
          >
            Connect Stripe
          </Button>
        }
      >
        <AlertTitle sx={{ fontWeight: 700 }}>
          Connect your Stripe account to start accepting payments
        </AlertTitle>
        Your account is set up, but you haven't connected a Stripe account yet.
        Connect Stripe to enable payment processing for client transactions.
      </Alert>
    </Box>
  );
};
